import { useEffect, useRef, useState } from 'react';
import { X, Lock } from 'lucide-react';
import useBoardStore from '../../store/boardStore';

// Definir, trocar e remover a senha que o quadro pede para abrir.
//
// TEXTO DA INTERFACE — regra fixada na entrega: falar do COMPORTAMENTO, nunca
// prometer protecao de dados. Esta senha e um pedido de senha na abertura do
// quadro, conferido no servidor; ela nao esconde o conteudo de quem tem
// permissao de acesso por outros caminhos. Nada de "criptografado", "dados
// protegidos" ou "acesso seguro" neste arquivo.
//
// A senha atual e OPCIONAL no formulario: informa-la deixa qualquer pessoa que
// administra o quadro trocar/remover; sem ela, so o Proprietario consegue — e
// isso quem decide e o servidor, que devolve 403 com a mensagem certa. O
// cliente nao tenta adivinhar quem e Proprietario.

const MIN_PASSWORD = 4;

export default function BoardProtectionModal({ board, onClose }) {
  const setBoardProtection = useBoardStore((s) => s.setBoardProtection);
  const removeBoardProtection = useBoardStore((s) => s.removeBoardProtection);

  const alreadyProtected = !!board?.is_protected;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const firstRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  const busy = saving || removing;

  const save = async (e) => {
    e?.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`A senha precisa ter pelo menos ${MIN_PASSWORD} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('As duas senhas digitadas não são iguais.');
      return;
    }
    setSaving(true);
    try {
      await setBoardProtection(board.id, { password, currentPassword: currentPassword || undefined });
      onClose();
    } catch (err) {
      setError(err.message || 'Não foi possível salvar a senha.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setError(null);
    setRemoving(true);
    try {
      await removeBoardProtection(board.id, { currentPassword: currentPassword || undefined });
      onClose();
    } catch (err) {
      setError(err.message || 'Não foi possível remover a senha.');
    } finally {
      setRemoving(false);
    }
  };

  // So Escape. Enter NAO e tratado aqui de proposito: quem cuida dele e o
  // <form> abaixo, e isso muda o comportamento num caso que custou caro —
  // antes, com o foco em "Remover senha", Enter chamava save() e TROCAVA a
  // senha, fechando o modal como se tivesse removido. Com form + type nos
  // botoes, o navegador faz o certo sozinho: Enter num campo submete (trocar),
  // Enter num botao com foco aciona AQUELE botao.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
  };

  const field = 'w-full bg-main border border-border rounded px-3 py-2 text-sm text-text-primary placeholder-text-secondary/50 outline-none focus:border-accent/50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-[#1e2235] border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-text-primary">
            <Lock className="w-4 h-4 text-accent" />
            {alreadyProtected ? 'Senha do quadro' : 'Proteger quadro'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/10 text-text-secondary transition-colors" title="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={save}>
          <div className="px-5 py-4 space-y-4">
            <p className="text-xs text-text-secondary leading-relaxed">
              {alreadyProtected
                ? <>Este quadro pede uma senha para abrir. Todo mundo digita, inclusive você.</>
                : <>Quem abrir <strong className="text-text-primary">{board?.name}</strong> vai precisar digitar esta senha antes de ver o quadro — inclusive você.</>}
            </p>

            {alreadyProtected && (
              <label className="block">
                <span className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
                  Senha atual
                </span>
                <input
                  ref={firstRef}
                  type="password"
                  autoComplete="off"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Deixe em branco se não lembra"
                  className={field}
                />
                <span className="block mt-1.5 text-[11px] text-text-secondary/80">
                  Sem informar a senha atual, só o Proprietário da conta troca ou
                  remove — ou, se a organização ainda não tem um, um administrador.
                </span>
              </label>
            )}

            <label className="block">
              <span className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
                {alreadyProtected ? 'Nova senha' : 'Senha'}
              </span>
              <input
                ref={alreadyProtected ? undefined : firstRef}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`Mínimo de ${MIN_PASSWORD} caracteres`}
                className={field}
              />
            </label>

            <label className="block">
              <span className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
                Repetir a senha
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={field}
              />
            </label>

            {error && (
              <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-5 py-4 border-t border-border shrink-0">
            {alreadyProtected && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-danger hover:bg-danger/10 border border-danger/30 transition-colors disabled:opacity-50"
              >
                {removing ? 'Removendo...' : 'Remover senha'}
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            {/* type="submit": e o unico botao de submit do form, entao Enter num
                campo cai aqui — e so aqui. */}
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-1.5 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent/80 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando...' : (alreadyProtected ? 'Trocar senha' : 'Proteger')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
